import React, { forwardRef } from 'react';
import { StyleSheet, TouchableHighlight, ViewStyle } from 'react-native';

import { Theme } from '@constants';
import { useContextReference } from '@hooks';

import { isAndroid, isWeb } from '../../../helpers/Platform';
import { FocusableHighlight, FocusableHighlightProps } from '../FocusableHighlight/FocusableHighlight';
import { TWCTileContent, TWCTileContentProps } from '../TWCTileContent/TWCTileContent';

export const TWCTile = forwardRef<TouchableHighlight, TWCTileProps>(
  (
    {
      accessibilityHint,
      accessibilityLabel,
      accessibilityRole,
      accessibilityLiveRegion,
      accessibilityActions,
      onAccessibilityAction,
      accessibilityValue,
      backgroundDefault,
      children,
      gradientType,
      reverseGradient,
      height,
      nativeID,
      onFocus,
      onPress,
      onBlur,
      testID,
      useLinearGradient,
      width,
      nextFocusDown,
      nextFocusLeft,
      nextFocusRight,
      nextFocusUp,
      hasTVPreferredFocus = false,
      shouldUseContextReference = true,
      styleFocused,
      useFocusBorder = true,
      borderStyleFocused,
    },
    reference,
  ) => {
    const contextReference = useContextReference<TouchableHighlight>(testID);

    return (
      <FocusableHighlight
        accessibilityActions={accessibilityActions}
        accessibilityHint={accessibilityHint}
        accessibilityLabel={accessibilityLabel}
        accessibilityLiveRegion={accessibilityLiveRegion}
        accessibilityRole={accessibilityRole}
        accessibilityValue={accessibilityValue}
        hasTVPreferredFocus={hasTVPreferredFocus}
        nativeID={nativeID || testID}
        nextFocusDown={nextFocusDown}
        nextFocusLeft={nextFocusLeft}
        nextFocusRight={nextFocusRight}
        nextFocusUp={nextFocusUp}
        ref={shouldUseContextReference ? contextReference : reference}
        style={[
          styles.focusableHighlightStyle,
          {
            /**
             * 12px is added to dimensions of the container to account for the focus border style
             * Per comps, borders appear outside of the element's actual dimensions
             */
            height: height ? Theme.px(height + 12) : isWeb ? 'max-content' : 'auto',
            width: width ? Theme.px(width + 12) : isWeb ? 'max-content' : 'auto',
          },
          !width && isAndroid && styles.focusableHighlightAlign,
        ]}
        styleFocused={useFocusBorder && [Theme.styles.border, borderStyleFocused]}
        testID={testID}
        underlayColor={Theme.transparent}
        onAccessibilityAction={onAccessibilityAction}
        onBlur={onBlur}
        onFocus={onFocus}
        onPress={onPress}
      >
        <TWCTileContent
          backgroundDefault={backgroundDefault}
          children={children}
          gradientType={gradientType}
          height={height}
          reverseGradient={reverseGradient}
          styleFocused={styleFocused}
          useFocusBorder={useFocusBorder}
          useLinearGradient={useLinearGradient}
          width={width}
        />
      </FocusableHighlight>
    );
  },
);

const styles = StyleSheet.create({
  focusableHighlightStyle: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  focusableHighlightAlign: {
    alignSelf: 'flex-start',
  },
});

export type TWCTileProps = {
  shouldUseContextReference?: boolean;
  borderStyleFocused?: ViewStyle;
} & TWCTileContentProps &
  FocusableHighlightProps;
